class ToastManager {
	maxToasts: number;
	toastDuration: number;
	animationDuration: number;
	toasts: string[];
	toastPortal: HTMLElement;
	toastRoot: HTMLElement;

	constructor( maxToasts = 3 ) {
		this.maxToasts = maxToasts;
		this.toastDuration = 5000;
		this.animationDuration = 300;
		this.toasts = [];
		this.toastPortal = document.getElementById(
			'tailwind-portal'
		) as HTMLElement;
		this.toastRoot =
			this.toastPortal &&
			( document.getElementById( 'toast-root' ) as HTMLElement );

		if ( ! this.toastPortal ) {
			this.toastPortal = document.createElement( 'div' );
			this.toastPortal.id = 'tailwind-portal';
			this.toastPortal.classList.add( 'tailwind' );
			document.body.appendChild( this.toastPortal );
		}

		if ( ! this.toastRoot ) {
			this.toastRoot = document.createElement( 'div' );
			this.toastRoot.id = 'toast-root';
			this.toastRoot.className =
				'tw-fixed tw-bottom-4 tw-right-4 tw-z-[9999]';
			this.toastPortal.appendChild( this.toastRoot );
		}
	}

	showToast( {
		title,
		description,
		status = 'default',
	}: {
		title: string;
		description: string;
		status?: string;
	} ) {
		const id = `toast-${ Date.now() }`;
		const toast = this._createToastElement( id, {
			title,
			description,
			status,
		} );

		this.toasts.push( id );
		if ( this.toasts.length > this.maxToasts ) {
			this._removeToast( this.toasts.shift() as string );
		}

		this.toastRoot.appendChild( toast );
		this._animateToastIn( toast );

		// Update the toast stack after the animation duration
		setTimeout( () => this._updateToastStack(), this.animationDuration );

		// Remove the toast after the duration, when -1, it will not remove
		if ( this.toastDuration > 0 ) {
			setTimeout( () => this._removeToast( id ), this.toastDuration );
		}
	}

	_updateToastStack() {
		this.toasts = this.toasts.filter( ( id ) =>
			document.getElementById( id )
		);
		this.toasts.reverse().forEach( ( id, index ) => {
			const toast = document.getElementById( id );
			if ( ! toast ) return;
			toast.style.transform = `translateY(${ index * -10 }px) scale(${
				1 - index * 0.05
			})`;
			toast.style.zIndex = ( 9999 - index ) as unknown as string;
			toast.style.opacity = ( 1 - index * 0.1 ) as unknown as string;
			//blur
			toast.style.filter = `blur(${ index * 0.5 }px)`;

			setTimeout( () => {}, this.animationDuration );
		} );
	}

	_createToastElement(
		id: string,
		{
			title,
			description,
			status,
		}: { title: string; description: string; status: string }
	) {
		const toast = document.createElement( 'div' );

		// Add status-specific classes
		const statusStyles = {
			default: 'tw-border-gray-200',
			success: 'tw-border-green-500',
			error: 'tw-border-red-500',
		};

		const titleColors = {
			default: 'tw-text-gray-800',
			success: 'tw-text-green-700',
			error: 'tw-text-red-700',
		};

		toast.className = `
        tw-absolute tw-right-0 tw-min-w-96 tw-bottom-0 tw-bg-white tw-text-black tw-rounded-lg tw-border tw-shadow-xl tw-px-4 tw-py-3
        tw-transition-all tw-opacity-0 tw-transform tw-translate-y-4 tw-flex tw-items-start
        ${
			statusStyles[ status as keyof typeof statusStyles ] ||
			statusStyles.default
		}
      `;
		toast.style.transition = `transform ${ this.animationDuration }ms ease, opacity ${ this.animationDuration }ms ease`;
		toast.id = id;

		const closeButton = document.createElement( 'span' );
		closeButton.innerHTML = '&times;';
		closeButton.className = `
        tw-ml-auto tw-text-gray-400 hover:tw-text-new-gray-200
        tw-transition-colors tw-cursor-pointer
      `;
		closeButton.onclick = () => this._removeToast( id );

		toast.innerHTML = `
        <div>
          <div class="tw-font-bold tw-text-lg tw-mb-1 ${
				titleColors[ status as keyof typeof titleColors ] ||
				titleColors.default
			}">${ title }</div>
          <div class="tw-text-sm tw-leading-snug">${ description }</div>
        </div>
      `;
		toast.appendChild( closeButton );
		return toast;
	}

	_animateToastIn( toast: HTMLElement ) {
		setTimeout( () => {
			toast.classList.remove( 'tw-opacity-0', 'tw-translate-y-4' );
			toast.classList.add( 'tw-opacity-100', 'tw-translate-y-0' );
		}, this.animationDuration );
	}

	_removeToast( id: string ) {
		const toast = document.getElementById( id );
		if ( ! toast ) return;
		toast.style.opacity = '';
		toast.style.transform = '';
		toast.classList.remove( 'tw-opacity-100', 'tw-translate-y-0' );
		toast.classList.add( 'tw-opacity-0', 'tw-translate-y-4' );

		setTimeout( () => {
			toast.remove();
			this.toasts = this.toasts.filter( ( toastId ) => toastId !== id );
			this._updateToastStack();
		}, 300 );
	}
}

declare global {
	interface Window {
		__toast: ( options: {
			title: string;
			description: string;
			status?: string;
		} ) => void;
		__toastManager: ToastManager;
	}
}

window.__toast = ( options: {
	title: string;
	description: string;
	status?: string;
} ) => {
	if ( ! window.__toastManager ) {
		window.__toastManager = new ToastManager( 3 ); // Default max of 3 toasts
	}
	window.__toastManager.showToast( options );
};
